// Seeded schema registry (PLAN §2): default types with ID prefixes and colors, the Actor Profile Sheet v2.0
// template (+ Person/Organization variants), an Event template, value lists (Camp, Status, Investigation status,
// rating scale + canonical rating dimensions…), relation types, event sub-types and participation roles.
// Rating dimensions, Camp/Status spellings and table headings come from the 60 real sheets in evals/real/.
// The seed migration is generated from this file (`pnpm schema:seed`); admins edit everything afterwards.

import type {
  DefaultClaimKind,
  EntityKind,
  EntityTypeDef,
  EnumListDef,
  EnumValueDef,
  FieldDef,
  FieldGroupDef,
  FieldType,
  ProfileTemplateDef,
  RelationCategory,
  RelationTypeDef,
  SchemaSnapshot,
  TableColumn,
  TemplateBlock,
} from './types';

/** Seed rows are dated to the registry launch; ids are readable and stable (`type_media`, `fld_camp`…). */
export const SEED_TIME = 1790294400000;

// ─── Entity types ────────────────────────────────────────────────────────────

type T = [
  key: string,
  label: string,
  parent: string | null,
  prefix: string | null,
  slot: number | null,
  icon?: string,
  description?: string,
];
const TYPES: Record<EntityKind, T[]> = {
  actor: [
    ['actor', 'Actor', null, null, 1, 'users', 'People and organizations.'],
    ['person', 'Person', 'actor', 'POL_', null, 'user', 'An individual: politician, official, journalist, activist, lawyer…'],
    ['organization', 'Organization', 'actor', 'ORG_', 2, 'building', 'Associations, networks, lobbies, institutions.'],
    ['media', 'Media', 'organization', 'MED_', 5, 'newspaper', 'Press titles, broadcasters, online media.'],
    ['company', 'Company', 'organization', null, 10, 'briefcase'],
    ['government_body', 'Government body', 'organization', null, 7, 'landmark'],
    ['party', 'Political party', 'organization', null, 11, 'flag'],
    ['ngo', 'NGO / association', 'organization', null, 3, 'hand-heart'],
    ['think_tank', 'Think tank', 'organization', null, 12, 'lightbulb'],
    ['foundation', 'Foundation', 'organization', null, null, 'gem'],
  ],
  event: [
    ['event', 'Event', null, 'EVT_', 4, 'calendar'],
    ['publication', 'Publication', 'event', null, null, 'book-open', 'An issue, article or video.'],
    ['statement', 'Statement', 'event', null, null, 'quote', 'On TV, radio or social media.'],
    ['conference', 'Conference / event', 'event', null, null, 'presentation'],
    ['meeting', 'Meeting', 'event', null, null, 'handshake'],
    ['hearing', 'Hearing', 'event', null, null, 'gavel'],
    ['vote', 'Vote', 'event', null, null, 'vote', 'A roll-call vote.'],
    ['resolution', 'Resolution / report / amendment', 'event', null, null, 'file-text'],
    ['trip', 'Trip / delegation visit', 'event', null, null, 'plane'],
    ['call', 'Call / webinar', 'event', null, null, 'video'],
    ['broadcast', 'Broadcast', 'event', null, null, 'radio'],
    ['foia_release', 'FOIA release', 'event', null, null, 'file-search', 'Documents released under a freedom-of-information request.'],
    ['launch', 'Launch / founding', 'event', null, null, 'rocket'],
    ['ownership_change', 'Ownership change', 'event', null, null, 'repeat'],
    ['career_move', 'Career move', 'event', null, null, 'arrow-right-left', 'e.g. "joined ELNET Paris".'],
    ['controversy', 'Controversy / incident', 'event', null, null, 'triangle-alert'],
    // Direct harms (migration 0052): who carried them out and who was targeted are participation claims.
    [
      'legal_action',
      'Legal action / lawfare',
      'event',
      null,
      null,
      'scale',
      'Lawsuits, complaints, prosecutions, bans, legal threats and criminalisation aimed at people or organizations.',
    ],
    [
      'surveillance',
      'Surveillance / spyware',
      'event',
      null,
      null,
      'eye',
      'Spyware, monitoring, data collection or infiltration aimed at people or organizations.',
    ],
    [
      'online_interference',
      'Online interference / disinformation',
      'event',
      null,
      null,
      'megaphone',
      'Disinformation, harassment or smear campaigns, bot networks, and manipulation of platforms or AI.',
    ],
    // Public registers (European Parliament connector): who asked or declared is a participation claim.
    [
      'written_question',
      'Written question',
      'event',
      null,
      null,
      'message-square-text',
      'A question an MEP puts in writing to the Commission or the Council, with every co-signer.',
    ],
    [
      'declared_meeting',
      'Declared meeting',
      'meeting',
      null,
      null,
      'notebook-pen',
      'A meeting an MEP, a Commissioner or an official declared publicly, with the interest representatives met.',
    ],
  ],
  location: [
    ['place', 'Place', null, 'PLC_', 6, 'map-pin'],
    ['country', 'Country', 'place', null, null, 'globe'],
    ['city', 'City', 'place', null, null, 'building-2'],
    ['venue', 'Venue', 'place', null, null, 'door-open'],
    ['address', 'Address', 'place', null, null, 'mail'],
  ],
  asset: [
    ['asset', 'Asset / transaction', null, 'AST_', 8, 'coins'],
    ['donation', 'Donation', 'asset', null, null, 'gift'],
    ['grant', 'Grant / subsidy', 'asset', null, null, 'badge-euro'],
    ['contract', 'Contract', 'asset', null, null, 'file-signature'],
    ['property', 'Property', 'asset', null, null, 'home'],
    ['shareholding', 'Shareholding', 'asset', null, null, 'pie-chart'],
    ['vehicle', 'Vehicle', 'asset', null, null, 'car'],
    ['bank_transfer', 'Bank transfer', 'asset', null, null, 'banknote'],
  ],
  document: [
    ['source', 'Source', null, 'SRC_', 9, 'file'],
    ['pdf', 'PDF', 'source', null, null, 'file-text'],
    ['image', 'Image', 'source', null, null, 'image'],
    ['audio', 'Audio', 'source', null, null, 'audio-lines'],
    ['video', 'Video', 'source', null, null, 'film'],
    ['dataset', 'Dataset', 'source', null, null, 'table'],
    ['web_page', 'Web page', 'source', null, null, 'globe'],
    ['transcript', 'Transcript', 'source', null, null, 'messages-square'],
  ],
  case: [['case', 'Case', null, 'CASE_', null, 'folder-kanban', 'An investigation grouping entities, sources and notes.']],
};

const typeId = (key: string) => `type_${key}`;

// ─── Value lists ─────────────────────────────────────────────────────────────

type V = [key: string, label: string, opts?: { slot?: number; score?: number; synonyms?: string[]; description?: string }];
type L = { key: string; label: string; description: string; values: V[] };
const LISTS: L[] = [
  {
    key: 'camp',
    label: 'Camp',
    description:
      'Where an actor stands. Imported values outside the list are mapped in review; the original text is kept as a Camp note. Colors feed the "Color by Camp" rule preset.',
    values: [
      ['adverse', 'Adverse', { slot: 8 }],
      ['ally', 'Ally', { slot: 6 }],
      ['pivot', 'Pivot', { slot: 4, description: 'Could move either way; found in the samples.' }],
      ['neutral', 'Neutral', { slot: 9 }],
      ['unknown', 'Unknown', { synonyms: ['unclear', 'n/a', 'not established'] }],
    ],
  },
  {
    key: 'entity_status',
    label: 'Status',
    description: 'Whether the actor is still active. Qualifiers ("ACTIVE (reduced activity since 2018)") are kept as a Status note.',
    values: [
      ['active', 'Active', { synonyms: ['current', 'in office'] }],
      ['formerly_active', 'Formerly active', { synonyms: ['former', 'formerly'] }],
      ['inactive', 'Inactive', { synonyms: ['dormant', 'dissolved', 'defunct', 'retired'] }],
      ['unverified', 'Unverified', { synonyms: ['unconfirmed'] }],
      ['deceased', 'Deceased', { synonyms: ['dead', 'died'] }],
    ],
  },
  {
    key: 'investigation_status',
    label: 'Investigation status',
    description: 'Workflow field on every entity (from the pilot list): filterable and usable in color rules.',
    values: [
      ['to_investigate', 'To investigate', { slot: 9, synonyms: ['to do', 'todo', 'new'] }],
      ['high_priority', 'High priority', { slot: 8, synonyms: ['priority', 'urgent'] }],
      ['to_complete', 'To complete', { slot: 4, synonyms: ['in progress', 'incomplete', 'partial'] }],
      ['complete', 'Complete', { slot: 6, synonyms: ['completed', 'done', 'finished'] }],
    ],
  },
  {
    key: 'monitoring_priority',
    label: 'Monitoring priority',
    description: 'Block 10. A plain field: nothing is automated from it.',
    values: [
      ['high', 'High', { slot: 8, synonyms: ['critical', 'top', 'very high'] }],
      ['medium', 'Medium', { slot: 4, synonyms: ['moderate', 'normal'] }],
      ['low', 'Low', { slot: 9, synonyms: ['minimal', 'very low'] }],
    ],
  },
  {
    key: 'rating_scale',
    label: 'Rating scale',
    description:
      'Words accepted for influence ratings and the 1–5 value each is stored as. The original wording is always kept ("High Risk" → High, note "Risk").',
    values: [
      ['low', 'Low', { score: 1, synonyms: ['very low', 'minimal', 'weak', 'negligible', 'limited', 'none'] }],
      ['low_medium', 'Low-Medium', { score: 2, synonyms: ['low to medium', 'medium-low', 'low-moderate', 'moderate-low', 'low/medium'] }],
      ['medium', 'Medium', { score: 3, synonyms: ['moderate', 'mid', 'average', 'intermediate'] }],
      [
        'medium_high',
        'Medium-High',
        { score: 4, synonyms: ['medium to high', 'high-medium', 'moderate-high', 'moderately high', 'medium/high'] },
      ],
      ['high', 'High', { score: 5, synonyms: ['very high', 'strong', 'major', 'significant', 'extensive', 'critical'] }],
    ],
  },
  {
    key: 'rating_dimension',
    label: 'Rating dimensions',
    description:
      'Canonical Block 4 dimensions. Sheets name dimensions freely (~90 names across 60 sheets); each rating keeps its name as written and is mapped to one of these through the synonyms, so actors can be compared.',
    values: [
      [
        'financial',
        'Financial',
        { synonyms: ['financial resources', 'financial influence', 'financial capacity', 'funding', 'financial power'] },
      ],
      [
        'human',
        'Human',
        { synonyms: ['human resources', 'organizational resources', 'organisational resources', 'staff', 'human capital'] },
      ],
      ['media', 'Media', { synonyms: ['media influence', 'media resources', 'media presence', 'media reach', 'media capital'] }],
      [
        'political',
        'Political / institutional',
        {
          synonyms: [
            'political influence',
            'political resources',
            'political capital',
            'political',
            'institutional influence',
            'institutional resources',
            'institutional reach',
            'institutional power',
            'institutional',
            'policy influence',
            'vote influence',
            'legislative output',
            'legislative impact',
            'structural influence',
          ],
        },
      ],
      [
        'legal',
        'Legal',
        { synonyms: ['legal capacity', 'legal resources', 'legal judicial influence', 'legal influence', 'normative legal impact'] },
      ],
      [
        'network',
        'Network',
        {
          synonyms: [
            'network strength',
            'network influence',
            'network reach',
            'network resources',
            'network centrality',
            'network value',
            'network access',
            'network embeddedness',
            'longevity embeddedness',
          ],
        },
      ],
      [
        'alignment',
        'Documented alignment',
        {
          synonyms: [
            'pro israel alignment',
            'pro israel consistency',
            'pro israel positioning',
            'pro israel orientation',
            'pro israel organizational ties',
            'documented alignment',
          ],
        },
      ],
      [
        'controversy',
        'Controversy risk',
        { synonyms: ['controversy risk', 'controversy', 'controversy vulnerability', 'radicalization trajectory'] },
      ],
      [
        'transparency',
        'Transparency',
        { synonyms: ['transparency exposure', 'transparency compliance', 'transparency disclosure', 'transparency non disclosure'] },
      ],
      ['overall', 'Overall', { synonyms: ['overall assessment', 'global'] }],
    ],
  },
  {
    key: 'level_of_action',
    label: 'Level of action',
    description: 'Where the actor operates. Several can apply.',
    values: [
      ['local', 'Local', { synonyms: ['municipal', 'city'] }],
      ['regional', 'Regional'],
      ['national', 'National'],
      ['european', 'European', { synonyms: ['eu', 'european union', 'european parliament'] }],
      ['international', 'International', { synonyms: ['global', 'transatlantic', 'worldwide'] }],
    ],
  },
  {
    key: 'verification_flag',
    label: 'Verification',
    description: 'How well a row is documented (Block 3 status, Block 5 "Verified?", allegations).',
    values: [
      ['documented', 'Documented', { slot: 6, synonyms: ['✓', 'yes', 'confirmed', 'verified', 'documented'] }],
      ['semi_documented', 'Semi-documented', { slot: 4, synonyms: ['partial', 'partially', 'partly', 'semi documented'] }],
      ['unverified', 'Unverified', { slot: 8, synonyms: ['no', '?', 'unconfirmed', 'not verified', 'alleged'] }],
    ],
  },
  {
    key: 'influence_category',
    label: 'Influence category',
    description: 'Block 3 row category.',
    values: [
      ['media', 'Media'],
      ['political', 'Political', { synonyms: ['policy'] }],
      ['events', 'Events', { synonyms: ['event'] }],
      ['legal', 'Legal', { synonyms: ['judicial'] }],
      ['financial', 'Financial', { synonyms: ['funding'] }],
      ['institutional', 'Institutional'],
      ['lobbying', 'Lobbying', { synonyms: ['advocacy'] }],
      ['academic', 'Academic', { synonyms: ['research'] }],
      ['digital', 'Digital / online', { synonyms: ['online', 'social media', 'digital'] }],
      ['other', 'Other'],
    ],
  },
  // Added for the influence mapping report (migration 0046): the level of the system an actor works at.
  {
    key: 'level',
    label: 'Level',
    description: 'The level of the system of influence an actor works at. Used by the Influence mapping report template.',
    values: [
      ['strategic', 'Strategic', { synonyms: ['strategy', 'strategic level', 'leadership', 'stratégique', 'niveau stratégique'] }],
      [
        'operational',
        'Operational',
        { synonyms: ['operations', 'operational level', 'opérationnel', 'opérationnelle', 'niveau opérationnel'] },
      ],
      ['relay', 'Relay', { synonyms: ['relays', 'relay level', 'amplifier', 'relais', 'niveau relais'] }],
      ['field', 'Field', { synonyms: ['ground', 'field level', 'grassroots', 'terrain', 'niveau terrain'] }],
      [
        'repressive',
        'Repressive',
        { synonyms: ['repression', 'repressive level', 'enforcement', 'répressif', 'répressive', 'répression'] },
      ],
    ],
  },
  // Added for the synthesis of a report (migration 0063, D460): the link an actor is in the chain of influence.
  {
    key: 'chain_link',
    label: 'Chain link',
    description:
      'The link an actor is in the chain of influence: the means and principals who pay and direct, the access relays who open doors, the institutional pivots who decide, and the framing and pressure that shapes the debate. Used by the coverage annex of a report.',
    values: [
      [
        'means_and_principals',
        'Means and principals',
        {
          synonyms: [
            'means',
            'principal',
            'principals',
            'funder',
            'sponsor',
            'moyens et commanditaires',
            'commanditaire',
            'commanditaires',
            'moyens',
          ],
        },
      ],
      [
        'access_relays',
        'Access relays',
        { synonyms: ['relay', 'access relay', 'access', 'intermediary', 'relais d’accès', "relais d'acces", 'relais', 'intermédiaire'] },
      ],
      [
        'institutional_pivots',
        'Institutional pivots',
        { synonyms: ['pivot', 'institutional pivot', 'decision maker', 'pivots institutionnels', 'pivot institutionnel', 'décideur'] },
      ],
      [
        'framing_and_pressure',
        'Framing and pressure',
        { synonyms: ['framing', 'pressure', 'narrative', 'cadrage et pression', 'cadrage', 'pression'] },
      ],
    ],
  },
];

// ─── Fields and templates ────────────────────────────────────────────────────

type F = {
  key: string;
  label: string;
  type: FieldType;
  help: string;
  placeholder?: string;
  formatHint?: string;
  enumList?: string;
  multi?: boolean;
  multilingual?: boolean;
  required?: boolean;
  claimKind?: DefaultClaimKind;
  columns?: TableColumn[];
  ratingScale?: string;
  justification?: boolean;
};
type G = { type: string; key: string; label: string; fields: F[] };

const col = (key: string, label: string, type: TableColumn['type'], extra: Partial<TableColumn> = {}): TableColumn => ({
  key,
  label,
  type,
  ...extra,
});
const DATE_HINT = 'A day, month, year, range or open period: 2021-10-06, Oct 2021, 2019–2024, since 2021, c. 2021.';
const AMOUNT_HINT = 'Exact (€50,000), a range (€2–10M), an estimate (est. $1M) or "undisclosed". The text you type is kept.';

const GROUPS: G[] = [
  {
    type: 'actor',
    key: 'header',
    label: 'Header',
    fields: [
      {
        key: 'camp',
        label: 'Camp',
        type: 'enum',
        enumList: 'camp',
        help: 'Where the actor stands. Values outside the list are kept as a Camp note.',
        placeholder: 'e.g. Adverse',
      },
      {
        key: 'camp_note',
        label: 'Camp note',
        type: 'text',
        help: 'The original Camp wording when it had more detail than the list value.',
        placeholder: 'e.g. PRO-ISRAEL / EJC POLICY',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'enum',
        enumList: 'entity_status',
        help: 'Whether the actor is still active.',
        placeholder: 'e.g. Active',
      },
      {
        key: 'status_note',
        label: 'Status note',
        type: 'text',
        help: 'Detail on the status.',
        placeholder: 'e.g. outside the Assemblée nationale since June 2024',
      },
      {
        key: 'investigation_status',
        label: 'Investigation status',
        type: 'enum',
        enumList: 'investigation_status',
        help: 'Where the research on this entity stands.',
        placeholder: 'e.g. To investigate',
      },
    ],
  },
  {
    type: 'actor',
    key: 'identification',
    label: 'Identification',
    fields: [
      {
        key: 'full_name',
        label: 'Full name',
        type: 'text',
        multilingual: true,
        help: 'The full, official name, in the original script.',
        placeholder: 'e.g. Franc-Tireur (hebdomadaire politique français)',
      },
      {
        key: 'sub_type',
        label: 'Sub-type',
        type: 'text',
        help: 'Free description of what kind of actor this is.',
        placeholder: 'e.g. Weekly political magazine / press outlet',
      },
      {
        key: 'main_country',
        label: 'Main country',
        type: 'place_ref',
        multi: true,
        help: 'Country (or countries) where the actor is based.',
        placeholder: 'e.g. France',
      },
      {
        key: 'level_of_action',
        label: 'Level of action',
        type: 'multi_enum',
        enumList: 'level_of_action',
        help: 'Where the actor operates. Several can apply.',
        placeholder: 'e.g. National, European',
      },
      {
        key: 'website',
        label: 'Website',
        type: 'url',
        multi: true,
        help: 'Official website.',
        placeholder: 'e.g. https://www.franc-tireur.fr',
      },
      {
        key: 'social_handles',
        label: 'Social media',
        type: 'social_handles',
        help: 'Accounts per platform.',
        placeholder: 'e.g. X: @FrancTireurMag | Facebook: franctireurmag',
        formatHint: 'Separate accounts with | or new lines; links work too.',
      },
      {
        key: 'key_persons',
        label: 'Key persons',
        type: 'table',
        help: 'People with a role in the organization. Each person becomes a Person actor linked by a relation claim.',
        columns: [
          col('person', 'Person', 'entity_ref', { required: true }),
          col('role', 'Role / position', 'text'),
          col('since', 'Since', 'fuzzy_date'),
        ],
      },
      {
        key: 'other_affiliations',
        label: 'Other affiliations',
        type: 'table',
        help: 'Other organizations or people the actor is affiliated with.',
        columns: [col('actor', 'Actor', 'entity_ref', { required: true }), col('link', 'Link', 'text')],
      },
      {
        key: 'main_contact',
        label: 'Main contact',
        type: 'longtext',
        help: 'Address, email, phone of the main contact point.',
        placeholder: 'e.g. Siège: Levallois-Perret | contact@…',
      },
      // Added by migration 0046 (influence mapping report).
      {
        key: 'level',
        label: 'Level',
        type: 'enum',
        enumList: 'level',
        help: 'The level of the system the actor works at: strategic (sets the direction), operational (turns it into programmes and money), relay (carries it to the public and to decision-makers), field (acts on the ground) or repressive (sanctions, prosecutes or watches).',
        placeholder: 'e.g. Relay',
      },
      // Added by migration 0063 (synthesis of a report, D460).
      {
        key: 'chain_link',
        label: 'Chain link',
        type: 'enum',
        enumList: 'chain_link',
        help: 'The link the actor is in the chain of influence: means and principals (pays and directs), access relays (opens doors), institutional pivots (decides), framing and pressure (shapes the debate).',
        placeholder: 'e.g. Access relays',
      },
    ],
  },
  {
    type: 'actor',
    key: 'strategic_positioning',
    label: 'Strategic positioning',
    fields: [
      ['stated_objectives', 'Stated objectives', 'What the actor says it wants.'],
      ['strategic_interests', 'Strategic interests', 'What the actor actually pursues, as assessed.'],
      ['priority_themes', 'Priority themes', 'Main themes, separated by semicolons. They also become tags.'],
      ['ideological_framework', 'Ideological framework', 'The ideas and narratives the actor works within.'],
      ['alliance_type', 'Alliance type', 'Financial, ideological, event-based, political…'],
      ['geographic_origin', 'Geographic origin', 'Where and when the actor started.'],
      ['shared_resources', 'Shared resources', 'Infrastructure, funding or people shared with other actors.'],
    ].map(([key, label, help]) => ({
      key: key ?? '',
      label: label ?? '',
      help: help ?? '',
      type: 'longtext' as const,
      claimKind: 'assessment' as const,
    })),
  },
  {
    type: 'actor',
    key: 'modes_of_influence',
    label: 'Modes of influence',
    fields: [
      {
        key: 'modes_of_influence',
        label: 'Modes of influence',
        type: 'table',
        help: 'How the actor exerts influence: one row per method, with evidence and links to Key events.',
        columns: [
          col('category', 'Category', 'enum', { enumList: 'influence_category' }),
          col('method', 'Method', 'longtext', { required: true }),
          col('status', 'Status', 'enum', { enumList: 'verification_flag' }),
          col('evidence', 'Evidence', 'longtext'),
          col('events', 'Events', 'event_ref', { multiValued: true }),
        ],
      },
    ],
  },
  {
    type: 'actor',
    key: 'influence_ratings',
    label: 'Influence ratings',
    fields: [
      {
        key: 'influence_ratings',
        label: 'Influence ratings',
        type: 'rating',
        multi: true,
        ratingScale: 'rating_scale',
        justification: true,
        claimKind: 'assessment',
        help: 'One rating per dimension (financial, human, media, political, legal, network…), each with a mandatory justification.',
        placeholder: 'e.g. Medium',
        formatHint: 'Low, Low-Medium, Medium, Medium-High, High, or 1–5. Stored as 1–5 with your wording kept.',
      },
    ],
  },
  {
    type: 'actor',
    key: 'funding',
    label: 'Funding & financial resources',
    fields: [
      {
        key: 'funding_sources',
        label: 'Funding sources',
        type: 'table',
        help: 'Who funds the actor. Each row becomes a flow claim; amounts can be undisclosed or estimated.',
        columns: [
          col('source', 'Source', 'entity_ref', { required: true }),
          col('type', 'Type', 'text'),
          col('amount', 'Amount', 'money_range'),
          col('period', 'Period', 'fuzzy_date'),
          col('verified', 'Verified?', 'enum', { enumList: 'verification_flag' }),
        ],
      },
      {
        key: 'total_budget',
        label: 'Total estimated budget',
        type: 'money_range',
        help: 'Total budget or revenue, if known.',
        placeholder: 'e.g. est. €2–10M/year',
        formatHint: AMOUNT_HINT,
      },
      // Added with the lobby and funding registers (migration 0059).
      {
        key: 'lobbying_costs',
        label: 'Lobbying costs (declared)',
        type: 'money_range',
        multi: true,
        help: 'What the organization declares it spends on lobbying the EU institutions, per financial year, as in the EU Transparency Register.',
        placeholder: 'e.g. €200,000–299,999',
        formatHint: AMOUNT_HINT,
      },
    ],
  },
  {
    type: 'actor',
    key: 'key_events',
    label: 'Key events',
    fields: [
      {
        key: 'key_events',
        label: 'Key events',
        type: 'table',
        help: 'Documented actions in the mandatory format [Date] | [Named actor(s)] | [Event/output] | [Source]. Rows become Events with participation claims.',
        columns: [
          col('date', 'Date', 'fuzzy_date', { required: true }),
          col('actors', 'Actor(s)', 'entity_ref', { multiValued: true }),
          col('event', 'Event / output', 'event_ref', { required: true }),
          col('source', 'Source', 'text'),
        ],
      },
    ],
  },
  {
    type: 'actor',
    key: 'network_links',
    label: 'Network links',
    fields: [
      {
        key: 'network_links',
        label: 'Network links',
        type: 'table',
        help: 'Links to other actors. A row may carry several link types; each becomes a relation claim.',
        columns: [
          col('linked_actor', 'Linked actor', 'entity_ref', { required: true }),
          col('link_types', 'Link type(s)', 'text', { multiValued: true }),
          col('period', 'Period', 'fuzzy_date'),
          col('context', 'Event / context', 'longtext'),
          col('source', 'Source', 'text'),
        ],
      },
    ],
  },
  {
    type: 'actor',
    key: 'controversies',
    label: 'Controversies & documented incidents',
    fields: [
      {
        key: 'controversies',
        label: 'Controversies',
        type: 'table',
        help: 'Each row becomes an Event of type Controversy / incident.',
        columns: [
          col('period', 'Period', 'fuzzy_date'),
          col('title', 'Title', 'text', { required: true }),
          col('source', 'Source', 'text'),
          col('narrative', 'Narrative', 'longtext'),
        ],
      },
    ],
  },
  {
    type: 'actor',
    key: 'analysis_monitoring',
    label: 'Strategic analysis & monitoring',
    fields: [
      {
        key: 'strategic_analysis',
        label: 'Strategic analysis',
        type: 'longtext',
        claimKind: 'assessment',
        help: 'The analyst’s overall reading of the actor.',
      },
      {
        key: 'vulnerabilities',
        label: 'Vulnerabilities / pressure points',
        type: 'longtext',
        claimKind: 'assessment',
        help: 'Where the actor is exposed.',
      },
      {
        key: 'next_actions',
        label: 'Recommended next actions',
        type: 'longtext',
        claimKind: 'assessment',
        help: 'Research to do next. Plain text: nothing is automated.',
      },
      {
        key: 'monitoring_priority',
        label: 'Monitoring priority',
        type: 'enum',
        enumList: 'monitoring_priority',
        help: 'How closely to follow this actor. Plain field: nothing is automated.',
        placeholder: 'e.g. High',
      },
    ],
  },
  {
    type: 'person',
    key: 'personal',
    label: 'Personal details',
    fields: [
      {
        key: 'date_of_birth',
        label: 'Date of birth',
        type: 'fuzzy_date',
        help: 'As precise as known.',
        placeholder: "e.g. 1971-03-02 or 'March 1971'",
        formatHint: DATE_HINT,
      },
      {
        key: 'nationality',
        label: 'Nationality',
        type: 'place_ref',
        multi: true,
        help: 'Country (or countries) of citizenship.',
        placeholder: 'e.g. France',
      },
      {
        key: 'current_position',
        label: 'Current position',
        type: 'text',
        help: 'Main current role.',
        placeholder: 'e.g. Member of the European Parliament',
      },
      {
        key: 'political_group',
        label: 'Political group / party',
        type: 'text',
        help: 'Party or parliamentary group, if any.',
        placeholder: 'e.g. Renew Europe',
      },
      // Added by migration 0046 (influence mapping report).
      {
        key: 'alignment_score',
        label: 'Alignment score',
        type: 'number',
        claimKind: 'assessment',
        help: 'The analyst’s score of how closely the person’s documented votes and positions align with the actors studied: 0 none, 1 partial, 2 full.',
        placeholder: 'e.g. 2',
        formatHint: 'A number from 0 to 2.',
      },
    ],
  },
  {
    type: 'organization',
    key: 'registration',
    label: 'Legal & registration',
    fields: [
      {
        key: 'legal_form',
        label: 'Legal form',
        type: 'text',
        help: 'Association, foundation, company form…',
        placeholder: 'e.g. Association loi 1901',
      },
      {
        key: 'founded',
        label: 'Founded',
        type: 'fuzzy_date',
        help: 'When the organization was created.',
        placeholder: 'e.g. October 6, 2021',
        formatHint: DATE_HINT,
      },
      {
        key: 'headquarters',
        label: 'Headquarters',
        type: 'place_ref',
        help: 'Where the organization is based.',
        placeholder: 'e.g. Levallois-Perret',
      },
      {
        key: 'registration_number',
        label: 'Registration number',
        type: 'text',
        help: 'Company, charity or association number.',
        placeholder: 'e.g. RNA W751234567',
      },
      {
        key: 'eu_transparency_register',
        label: 'EU Transparency Register ID',
        type: 'text',
        help: 'ID in the EU Transparency Register, if registered.',
        placeholder: 'e.g. 123456789012-34',
      },
    ],
  },
  {
    type: 'event',
    key: 'event_details',
    label: 'Event details',
    fields: [
      {
        key: 'date',
        label: 'Date',
        type: 'fuzzy_date',
        required: true,
        help: 'When it happened (a range for multi-day events).',
        placeholder: 'e.g. 2024-03-19',
        formatHint: DATE_HINT,
      },
      { key: 'place', label: 'Place', type: 'place_ref', help: 'Where it happened.', placeholder: 'e.g. Paris' },
      { key: 'description', label: 'Description', type: 'longtext', help: 'What happened.' },
      {
        key: 'outcome',
        label: 'Output / outcome',
        type: 'longtext',
        help: 'What came out of it: a publication, a vote result, a statement.',
      },
      { key: 'url', label: 'Link', type: 'url', help: 'A page describing the event.', placeholder: 'e.g. https://…' },
    ],
  },
  {
    type: 'event',
    key: 'participants',
    label: 'Participants',
    fields: [
      {
        key: 'participants',
        label: 'Participants',
        type: 'table',
        help: 'Who took part and in what role (speaker, organizer, for/against in a vote…). Rows become participation claims.',
        columns: [
          col('actor', 'Actor', 'entity_ref', { required: true }),
          col('role', 'Role', 'text', { help: 'e.g. speaker, organizer, host, for, against' }),
        ],
      },
    ],
  },
  {
    type: 'place',
    key: 'place_details',
    label: 'Place details',
    fields: [
      { key: 'country', label: 'Country', type: 'place_ref', help: 'The country this place is in.', placeholder: 'e.g. Belgium' },
      { key: 'address', label: 'Address', type: 'longtext', help: 'Street address, if relevant.' },
      { key: 'coordinates', label: 'Coordinates', type: 'text', help: 'Latitude, longitude.', placeholder: 'e.g. 50.8467, 4.3525' },
    ],
  },
  {
    type: 'asset',
    key: 'asset_details',
    label: 'Asset / transaction details',
    fields: [
      {
        key: 'amount',
        label: 'Amount',
        type: 'money_range',
        help: 'Value of the asset or transaction.',
        placeholder: 'e.g. €50,000',
        formatHint: AMOUNT_HINT,
      },
      { key: 'date', label: 'Date', type: 'fuzzy_date', help: 'When it took place.', placeholder: 'e.g. 2023', formatHint: DATE_HINT },
      { key: 'from_actor', label: 'From', type: 'entity_ref', help: 'Who gave, paid or sold.' },
      { key: 'to_actor', label: 'To', type: 'entity_ref', help: 'Who received or bought.' },
      { key: 'description', label: 'Description', type: 'longtext', help: 'What the asset or transaction is.' },
    ],
  },
  {
    type: 'source',
    key: 'source_details',
    label: 'Source details',
    fields: [
      {
        key: 'publisher',
        label: 'Publisher / author',
        type: 'entity_ref',
        help: 'Who published or wrote it.',
        placeholder: 'e.g. Le Monde',
      },
      {
        key: 'published',
        label: 'Published',
        type: 'fuzzy_date',
        help: 'Date of the material (not the upload date).',
        placeholder: 'e.g. 2021-09-21',
        formatHint: DATE_HINT,
      },
      { key: 'url', label: 'Link', type: 'url', help: 'Where it can be found online.' },
      {
        key: 'reference_no',
        label: 'Reference number',
        type: 'text',
        help: 'Official reference, e.g. a FOIA number.',
        placeholder: 'e.g. Ares(2025)5463115',
      },
    ],
  },
  {
    type: 'case',
    key: 'case_details',
    label: 'Case details',
    fields: [
      { key: 'description', label: 'Description', type: 'longtext', help: 'What the investigation is about.' },
      { key: 'lead', label: 'Lead researcher', type: 'text', help: 'Who leads the case.' },
    ],
  },
  // Added by task 19 (migration 0022): statement claims from review step 2 need a field to live in.
  {
    type: 'actor',
    key: 'statements',
    label: 'Statements',
    fields: [
      {
        key: 'statements',
        label: 'Statements',
        type: 'longtext',
        multi: true,
        help: 'What the actor said or wrote, word for word in the original language, with a translation when it is not in English.',
      },
    ],
  },
];

const H = (...p: string[]) => p;
type B = Omit<TemplateBlock, 'order'> & { order: number };
const ACTOR_BLOCKS: B[] = [
  {
    key: 'header',
    label: 'Header',
    order: 0,
    group: 'header',
    builtins: ['code', 'display_name', 'type', 'reliability', 'updated'],
    layout: 'header',
    storedAs: 'Entity code + fields',
    import: { headingPatterns: H('^actor profile sheet', '^id\\s*:') },
  },
  {
    key: 'identification',
    number: 1,
    label: 'Identification',
    order: 10,
    group: 'identification',
    layout: 'fields',
    storedAs: 'Fields + relation claims (key persons → Person actors)',
    import: { headingPatterns: H('^block\\s*1\\b', 'identification') },
  },
  {
    key: 'strategic_positioning',
    number: 2,
    label: 'Strategic positioning',
    order: 20,
    group: 'strategic_positioning',
    layout: 'text',
    storedAs: 'Assessment claims; themes as tags',
    import: { headingPatterns: H('^block\\s*2\\b', 'strategic positioning') },
  },
  {
    key: 'modes_of_influence',
    number: 3,
    label: 'Modes of influence',
    order: 30,
    group: 'modes_of_influence',
    layout: 'table',
    storedAs: 'Table rows linked to Events',
    import: {
      headingPatterns: H('^block\\s*3\\b', 'modes of influence'),
      columns: { category: ['Category'], method: ['Method'], status: ['Status'], evidence: ['Evidence / Block 6 ref.', 'Evidence'] },
    },
  },
  {
    key: 'influence_ratings',
    number: 4,
    label: 'Influence ratings',
    order: 40,
    group: 'influence_ratings',
    layout: 'ratings',
    storedAs: 'Rating fields (dimension as written, 1–5, justification)',
    import: { headingPatterns: H('^block\\s*4\\b', 'influence ratings') },
  },
  {
    key: 'funding',
    number: 5,
    label: 'Funding & financial resources',
    order: 50,
    group: 'funding',
    layout: 'table',
    storedAs: 'Flow claims (amount may be undisclosed or estimated)',
    import: {
      headingPatterns: H('^block\\s*5\\b', 'funding'),
      columns: { source: ['Source'], type: ['Type'], amount: ['Amount'], period: ['Period'], verified: ['Verified?', 'Verified'] },
    },
  },
  {
    key: 'key_events',
    number: 6,
    label: 'Key events / documented actions',
    order: 60,
    group: 'key_events',
    layout: 'table',
    storedAs: 'Event entities + participation claims',
    import: {
      headingPatterns: H('^block\\s*6\\b', 'key events'),
      columns: {
        date: ['Date'],
        actors: ['Actor(s)', 'Actors'],
        event: ['Event / Output', 'Event/Output', 'Event'],
        source: ['Source', 'Source URL'],
      },
    },
  },
  {
    key: 'network_links',
    number: 7,
    label: 'Network links & inter-actor connections',
    order: 70,
    group: 'network_links',
    layout: 'table',
    storedAs: 'Relation claims (a row may carry several link types)',
    import: {
      headingPatterns: H('^block\\s*7\\b', 'network links'),
      columns: {
        linked_actor: ['Linked Actor', 'Actor'],
        link_types: ['Link Type', 'Link Type(s)', 'Link Types'],
        period: ['Date', 'Period'],
        context: ['Event/Context', 'Event / Context', 'Context'],
        source: ['Source'],
      },
    },
  },
  {
    key: 'controversies',
    number: 8,
    label: 'Controversies & documented incidents',
    order: 80,
    group: 'controversies',
    layout: 'table',
    storedAs: 'Events of type Controversy / incident',
    import: {
      headingPatterns: H('^block\\s*8\\b', 'controversies'),
      columns: { period: ['Period', 'Date'], title: ['Title'], source: ['Source'], narrative: ['Narrative', 'Description'] },
    },
  },
  {
    key: 'composite_score',
    number: 9,
    label: 'Composite score',
    order: 90,
    layout: 'fields',
    ignored: true,
    storedAs: 'Not implemented (dropped); ignored on import',
    import: { headingPatterns: H('^block\\s*9\\b', 'composite score') },
  },
  {
    key: 'analysis_monitoring',
    number: 10,
    label: 'Strategic analysis & monitoring',
    order: 100,
    group: 'analysis_monitoring',
    layout: 'text',
    storedAs: 'Assessment text fields + a priority field; no automation',
    import: { headingPatterns: H('^block\\s*10\\b', 'strategic analysis') },
  },
];

type Tpl = Omit<ProfileTemplateDef, 'createdAt' | 'active' | 'notes'> & { notes?: string };
const TEMPLATES: Tpl[] = [
  {
    id: 'tpl_actor_2_0',
    entityTypeId: typeId('actor'),
    name: 'Actor Profile Sheet',
    version: '2.0',
    parentTemplateId: null,
    blocks: ACTOR_BLOCKS,
    removedBlocks: [],
    boilerplateMarkings: ['CONFIDENTIAL — Internal use only'],
    notes: 'The program’s 10-block sheet (v2.0 May 2026). Block 9 is ignored.',
  },
  {
    id: 'tpl_person_2_0',
    entityTypeId: typeId('person'),
    name: 'Actor Profile Sheet — Person',
    version: '2.0',
    parentTemplateId: 'tpl_actor_2_0',
    blocks: [
      {
        key: 'personal',
        label: 'Personal details',
        order: 15,
        group: 'personal',
        layout: 'fields',
        import: { headingPatterns: H('personal details') },
      },
    ],
    removedBlocks: [],
    boilerplateMarkings: [],
  },
  {
    id: 'tpl_organization_2_0',
    entityTypeId: typeId('organization'),
    name: 'Actor Profile Sheet — Organization',
    version: '2.0',
    parentTemplateId: 'tpl_actor_2_0',
    blocks: [
      {
        key: 'registration',
        label: 'Legal & registration',
        order: 15,
        group: 'registration',
        layout: 'fields',
        import: { headingPatterns: H('legal (and|&) registration', 'registration') },
      },
    ],
    removedBlocks: [],
    boilerplateMarkings: [],
  },
  {
    id: 'tpl_event_1_0',
    entityTypeId: typeId('event'),
    name: 'Event',
    version: '1.0',
    parentTemplateId: null,
    blocks: [
      { key: 'header', label: 'Header', order: 0, builtins: ['code', 'display_name', 'type', 'reliability', 'updated'], layout: 'header' },
      { key: 'details', label: 'Event details', order: 10, group: 'event_details', layout: 'fields' },
      { key: 'participants', label: 'Participants', order: 20, group: 'participants', layout: 'table', storedAs: 'Participation claims' },
    ],
    removedBlocks: [],
    boilerplateMarkings: [],
  },
];

// ─── Relation types ──────────────────────────────────────────────────────────

type R = [
  key: string,
  label: string,
  inverse: string | null,
  opts?: {
    symmetric?: boolean;
    amount?: boolean;
    period?: boolean;
    group?: string;
    source?: EntityKind[];
    target?: EntityKind[];
    description?: string;
  },
];
const A: EntityKind[] = ['actor'];
const RELATIONS: Record<RelationCategory, R[]> = {
  relation: [
    ['owner_of', 'owner / financial backer of', 'owned / backed by', { amount: true, period: true }],
    ['employer_of', 'employer of', 'employed by', { period: true }],
    [
      'role_at',
      'holds a role at',
      'has in a role',
      { period: true, description: 'Key persons: "CEO of", "Editorial director of". The role is written on the claim.' },
    ],
    ['superior_of', 'direct superior of', 'reports to', { period: true }],
    ['colleague_of', 'colleague of', null, { symmetric: true, period: true }],
    ['co_host_with', 'co-host with', null, { symmetric: true, period: true }],
    ['member_of', 'member of', 'has member', { period: true }],
    ['sister_organization_of', 'sister organization of', null, { symmetric: true }],
    ['coalition_with', 'lobbying coalition with', null, { symmetric: true, period: true }],
    ['event_partner_of', 'event partner of', null, { symmetric: true, period: true }],
    ['ideologically_aligned_with', 'ideologically aligned with', null, { symmetric: true }],
    ['media_platform_for', 'gives a media platform to', 'has a media platform in', { period: true }],
    ['funded_by', 'funded by', 'funds', { amount: true, period: true }],
    ['adviser_to', 'adviser to', 'advised by', { period: true }],
    ['assistant_to', 'assistant to', 'assisted by', { period: true }],
    ['service_provider_to', 'service provider to', 'client of', { amount: true, period: true }],
    ['former_role_at', 'former role at (revolving door)', 'former staff', { period: true }],
  ],
  participation: [
    ['participant', 'participant in', 'participant', { source: A, target: ['event'] }],
    ['organizer', 'organizer of', 'organized by', { source: A, target: ['event'] }],
    ['speaker', 'speaker at', 'speaker', { source: A, target: ['event'] }],
    ['moderator', 'moderator of', 'moderated by', { source: A, target: ['event'] }],
    ['host', 'host of', 'hosted by', { source: A, target: ['event'] }],
    ['sponsor', 'sponsor of', 'sponsored by', { source: A, target: ['event'], amount: true }],
    ['funder', 'funder of', 'funded by', { source: A, target: ['event'], amount: true }],
    ['attendee', 'attendee of', 'attended by', { source: A, target: ['event'] }],
    ['author', 'author of', 'authored by', { source: A, target: ['event'] }],
    ['rapporteur', 'rapporteur of', 'rapporteur', { source: A, target: ['event'] }],
    ['subject', 'subject / target of', 'about', { source: A, target: ['event'] }],
    ['accuser', 'accuser in', 'accused by', { source: A, target: ['event'] }],
    ['owner', 'owner in', 'owner', { source: A, target: ['event'], description: 'e.g. the buyer in an ownership change.' }],
    ['vote_for', 'voted for', 'for', { source: A, target: ['event'], group: 'vote' }],
    ['vote_against', 'voted against', 'against', { source: A, target: ['event'], group: 'vote' }],
    ['vote_abstain', 'abstained on', 'abstain', { source: A, target: ['event'], group: 'vote' }],
    ['vote_absent', 'absent from', 'absent', { source: A, target: ['event'], group: 'vote' }],
  ],
  location: [
    ['held_at', 'held at', 'venue of', { source: ['event'], target: ['location'] }],
    ['based_in', 'based in', 'home of', { source: ['actor', 'asset'], target: ['location'], period: true }],
    ['located_in', 'located in', 'contains', { source: ['location'], target: ['location'] }],
  ],
  flow: [
    [
      'donated_to',
      'donated to',
      'received a donation from',
      { amount: true, period: true, description: 'Flow claim; may point at the donation as an Asset.' },
    ],
    ['paid', 'paid', 'was paid by', { amount: true, period: true }],
    ['invested_in', 'invested in', 'received investment from', { amount: true, period: true }],
    ['granted', 'gave a grant to', 'received a grant from', { amount: true, period: true }],
  ],
};

/**
 * Relation types added after the seed migration (0008), numbered after every seeded one so the rows already in a
 * database keep their order. Migration 0052: who carried out a harm event and who it targeted.
 */
const LATER_RELATIONS: [RelationCategory, ...R][] = [
  [
    'participation',
    'carried_out',
    'carried out',
    'carried out by',
    {
      source: A,
      target: ['event'],
      description: 'Who carried out a legal action, a surveillance operation or an online campaign: the perpetrator.',
    },
  ],
  [
    'participation',
    'targeted_by',
    'targeted by',
    'targeted',
    { source: A, target: ['event'], description: 'Who a legal action, a surveillance operation or an online campaign was aimed at.' },
  ],
  // Public registers: the first author of a written question is its `author`; the others co-signed it.
  [
    'participation',
    'co_signer',
    'co-signed',
    'co-signed by',
    { source: A, target: ['event'], description: 'A co-signer of a written question.' },
  ],
  [
    'participation',
    'declarant',
    'declared',
    'declared by',
    { source: A, target: ['event'], description: 'Who declared a meeting in a public register (an MEP, a Commissioner, a cabinet).' },
  ],
  [
    'participation',
    'interest_representative',
    'met as an interest representative in',
    'interest representative',
    { source: A, target: ['event'], description: 'An organization or person met at a declared meeting, as the register names it.' },
  ],
];

// ─── Build the snapshot ──────────────────────────────────────────────────────

function build(): SchemaSnapshot {
  const types: EntityTypeDef[] = Object.entries(TYPES).flatMap(([kind, rows]) =>
    rows.map(([key, label, parent, prefix, slot, icon, description], i) => ({
      id: typeId(key),
      kind: kind as EntityKind,
      key,
      label,
      description: description ?? null,
      icon: icon ?? null,
      colorSlot: slot,
      parentTypeId: parent ? typeId(parent) : null,
      codePrefix: prefix,
      order: i,
      archived: false,
    })),
  );
  const groups: FieldGroupDef[] = [];
  const fields: FieldDef[] = [];
  for (const [gi, g] of GROUPS.entries()) {
    const gid = `grp_${g.type}_${g.key}`;
    groups.push({ id: gid, entityTypeId: typeId(g.type), key: g.key, label: g.label, order: gi });
    for (const [i, f] of g.fields.entries())
      fields.push({
        id: `fld_${g.type}_${f.key}`,
        groupId: gid,
        key: f.key,
        label: f.label,
        dataType: f.type,
        multilingual: !!f.multilingual,
        multiValued: !!f.multi,
        required: !!f.required,
        enumList: f.enumList ?? null,
        ratingScale: f.ratingScale ?? null,
        justificationRequired: !!f.justification,
        columns: f.columns ?? null,
        claimKind: f.claimKind ?? 'fact',
        help: f.help,
        placeholder: f.placeholder ?? null,
        formatHint: f.formatHint ?? (f.type === 'fuzzy_date' ? DATE_HINT : f.type === 'money_range' ? AMOUNT_HINT : null),
        order: i,
        archived: false,
      });
  }
  const templates: ProfileTemplateDef[] = TEMPLATES.map(t => ({ ...t, notes: t.notes ?? null, active: true, createdAt: SEED_TIME }));
  const lists: EnumListDef[] = LISTS.map(l => {
    const id = `list_${l.key}`;
    return {
      id,
      key: l.key,
      label: l.label,
      description: l.description,
      system: true,
      values: l.values.map(
        ([key, label, o], i): EnumValueDef => ({
          id: `val_${l.key}_${key}`,
          listId: id,
          key,
          label,
          colorSlot: o?.slot ?? null,
          score: o?.score ?? null,
          synonyms: o?.synonyms ?? [],
          description: o?.description ?? null,
          order: i,
          archived: false,
        }),
      ),
    };
  });
  let order = 0;
  const relationRows: [RelationCategory, ...R][] = [
    ...Object.entries(RELATIONS).flatMap(([category, rows]) =>
      rows.map(r => [category as RelationCategory, ...r] as [RelationCategory, ...R]),
    ),
    ...LATER_RELATIONS,
  ];
  const relationTypes: RelationTypeDef[] = relationRows.map(([category, key, label, inverse, o]) => ({
    id: `rel_${key}`,
    key,
    label,
    inverseLabel: inverse,
    category,
    groupKey: o?.group ?? null,
    directed: !o?.symmetric,
    symmetric: !!o?.symmetric,
    sourceKinds: o?.source ?? A,
    targetKinds: o?.target ?? A,
    hasAmount: !!o?.amount,
    hasPeriod: !!o?.period,
    description: o?.description ?? null,
    order: order++,
    archived: false,
  }));
  return { types, groups, fields, templates, lists, relationTypes };
}

export const SEED_SCHEMA: SchemaSnapshot = build();
